import { useState } from 'react'
import { Eye, EyeOff, Loader2 } from 'lucide-react'
import { Button } from '@/common/ui/button'
import { OperatorGroupPermission } from '@/schema'
import useQueryGetOperatorMe from '@/modules/operator/api/hooks/useQueryGetMe'
import useQueryGetGeneralSetting from '@/modules/client-setting/api/hooks/useQueryGetGeneralSetting'
import { getPlayerFullPhone } from '@/modules/manage-player/api/services'

interface PhoneCellProps {
	playerId: string
	phone: string
}

export function PhoneCell({ playerId, phone }: PhoneCellProps) {
	const { data: operatorMe } = useQueryGetOperatorMe()
	const { data: generalSetting } = useQueryGetGeneralSetting()
	const [fullPhone, setFullPhone] = useState<string | null>(null)
	const [showFull, setShowFull] = useState(false)
	const [isLoading, setIsLoading] = useState(false)

	const isMaskEnabled = generalSetting?.enableMaskPhone ?? true

	const hasViewFullPhonePermission =
		operatorMe?.userGroup?.readPermission?.includes(
			OperatorGroupPermission.VIEW_FULL_PHONE,
		) ||
		operatorMe?.userGroup?.writePermission?.includes(
			OperatorGroupPermission.VIEW_FULL_PHONE,
		)

	if (!isMaskEnabled) {
		return <span>{phone}</span>
	}

	const handleToggle = async () => {
		if (showFull) {
			setShowFull(false)
			return
		}
		if (!fullPhone) {
			setIsLoading(true)
			try {
				const data = await getPlayerFullPhone(playerId)
				setFullPhone(data.phone)
				setShowFull(true)
			} finally {
				setIsLoading(false)
			}
		} else {
			setShowFull(true)
		}
	}

	return (
		<span className="inline-flex items-center gap-1">
			<span>{showFull && fullPhone ? fullPhone : phone}</span>
			{hasViewFullPhonePermission && (
				<Button
					variant="ghost"
					size="sm"
					onClick={handleToggle}
					disabled={isLoading}
					className="h-5 w-5 p-0 hover:bg-gray-100"
				>
					{isLoading ? (
						<Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />
					) : showFull ? (
						<EyeOff className="h-3.5 w-3.5 text-gray-500" />
					) : (
						<Eye className="h-3.5 w-3.5 text-gray-500" />
					)}
				</Button>
			)}
		</span>
	)
}
